// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Download, 
  File, 
  BookOpen, 
  Wrench, 
  Calendar, 
  User, 
  HardDrive, 
  Loader2,
  ExternalLink,
  Smartphone,
  FileText
} from 'lucide-react';
import { formatFileSize, formatDate, getFileIcon } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detect mobile device
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         window.innerWidth < 768;
};

export default function FilePreviewModal({ file, open, onOpenChange, onDownload, onFillForm }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [textContent, setTextContent] = useState(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    setIsMobileDevice(isMobile());
    
    const handleResize = () => setIsMobileDevice(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ref sempre aponta para a URL de blob mais recente, evitando revogar a URL errada no cleanup.
  const previewUrlRef = useRef(null);
  useEffect(() => { previewUrlRef.current = previewUrl; }, [previewUrl]);

  useEffect(() => {
    if (file && open) {
      loadPreview();
    } else {
      setPreviewUrl(null);
      setTextContent(null);
    }

    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
    // loadPreview is recreated every render; re-run only when file/open change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, open]);

  const loadPreview = async () => {
    if (!file) return;
    
    const isPdf = file.content_type === 'application/pdf';
    
    // For PDFs on mobile, don't load the blob - just show options
    if (isPdf && isMobile()) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setTextContent(null);
    
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, {
        responseType: 'blob'
      });
      
      const contentType = file.content_type || response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      
      // For text files, read the content
      if (contentType.startsWith('text/') || 
          file.original_filename?.endsWith('.txt') ||
          file.original_filename?.endsWith('.json') ||
          file.original_filename?.endsWith('.xml') ||
          file.original_filename?.endsWith('.csv') ||
          file.original_filename?.endsWith('.md')) {
        const text = await blob.text();
        setTextContent(text);
      } else {
        // For images and PDFs on desktop, create object URL
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPdfInBrowser = useCallback(async () => {
    if (!file) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Open in new tab/window
      window.open(url, '_blank');
      
      // Clean up after a delay
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error('Error opening PDF:', error);
    } finally {
      setLoading(false);
    }
  }, [file]);

  const downloadAndOpenPdf = useCallback(async () => {
    if (!file) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setLoading(false);
    }
  }, [file]);

  if (!file) return null;

  const FileIcon = getFileIcon(file.content_type);
  const isImage = file.content_type?.startsWith('image/');
  const isPdf = file.content_type === 'application/pdf';
  const isText = file.content_type?.startsWith('text/') || 
                 file.original_filename?.endsWith('.txt') ||
                 file.original_filename?.endsWith('.json') ||
                 file.original_filename?.endsWith('.xml') ||
                 file.original_filename?.endsWith('.csv') ||
                 file.original_filename?.endsWith('.md');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="file-preview-modal">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                file.category === 'congregacao' ? 'bg-blue-50' : 'bg-green-50'
              }`}>
                <FileIcon className={`w-6 h-6 ${
                  file.category === 'congregacao' ? 'text-blue-700' : 'text-green-700'
                }`} />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate pr-4 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {file.original_filename}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Visualização do arquivo {file.original_filename}
                </DialogDescription>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className={`text-xs ${
                    file.category === 'congregacao' 
                      ? 'border-blue-200 text-blue-700 bg-blue-50' 
                      : 'border-green-200 text-green-700 bg-green-50'
                  }`}>
                    {file.category === 'congregacao' ? (
                      <><BookOpen className="w-3 h-3 mr-1" /> Congregação</>
                    ) : (
                      <><Wrench className="w-3 h-3 mr-1" /> Manutenção</>
                    )}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* File Info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 py-3 sm:py-4 border-b border-slate-100 flex-shrink-0 text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Tamanho:</span>
            <span className="text-slate-900 font-medium">{formatFileSize(file.size)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Data:</span>
            <span className="text-slate-900 font-medium">{formatDate(file.created_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Por:</span>
            <span className="text-slate-900 font-medium truncate">{file.uploaded_by_name}</span>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto py-4 min-h-[200px] sm:min-h-[300px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-4" />
              <p className="text-slate-500">Carregando...</p>
            </div>
          ) : isImage && previewUrl ? (
            <div className="flex items-center justify-center h-full">
              <img 
                src={previewUrl}
                alt={file.original_filename}
                className="max-w-full max-h-[50vh] sm:max-h-[60vh] object-contain rounded-lg"
              />
            </div>
          ) : isPdf && isMobileDevice ? (
            /* Mobile PDF - Show options to open/download */
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mb-4">
                <File className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Documento PDF
              </h3>
              <p className="text-slate-500 mb-6 text-sm">
                Para visualizar o PDF no seu dispositivo, escolha uma opção:
              </p>
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <Button
                  onClick={openPdfInBrowser}
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  disabled={loading}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Abrir no Navegador
                </Button>
                <Button
                  onClick={downloadAndOpenPdf}
                  variant="outline"
                  className="w-full"
                  disabled={loading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar PDF
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-4 flex items-center gap-1">
                <Smartphone className="w-3 h-3" />
                Visualização otimizada para dispositivos móveis
              </p>
            </div>
          ) : isPdf && previewUrl ? (
            /* Desktop PDF - Use iframe */
            <iframe
              src={`${previewUrl}#toolbar=1&navpanes=0&scrollbar=1`}
              className="w-full h-[60vh] rounded-lg border border-slate-200"
              title={file.original_filename}
            />
          ) : isText && textContent !== null ? (
            <div className="bg-slate-50 rounded-lg p-4 h-[50vh] sm:h-[60vh] overflow-auto">
              <pre className="text-xs sm:text-sm text-slate-700 whitespace-pre-wrap font-mono">
                {textContent}
              </pre>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <File className="w-16 h-16 text-slate-300 mb-4" />
              <p className="text-slate-500 mb-2">
                Visualização não disponível
              </p>
              <p className="text-sm text-slate-400 mb-4">
                Tipo: {file.content_type || 'desconhecido'}
              </p>
              <Button
                onClick={() => onDownload(file)}
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar arquivo
              </Button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="close-preview-button"
          >
            Fechar
          </Button>
          {isPdf && onFillForm && (
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                onFillForm(file);
              }}
              data-testid="fill-form-button"
            >
              <FileText className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Preencher Formulário</span>
              <span className="sm:hidden">Preencher</span>
            </Button>
          )}
          <Button
            onClick={() => onDownload(file)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="download-preview-button"
          >
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Baixar Arquivo</span>
            <span className="sm:hidden">Baixar</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
